import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import useAuthToken from "../hooks/useAuthToken";
import { protectedResources } from "../authConfig";
import { createApiClient } from "../utils/apiClient";
import { useUser } from "../utils/userContext";
import type { LegalAcceptRequest, LegalStatusResponse } from "../types/apiSchemaAliases";
import WorkspaceLoading from "./WorkspaceLoading";
import styles from "./LegalAcceptanceGate.module.css";

const ACCEPTANCE_SOURCE = "existing_tenant_gate";

type LegalAcceptanceGateProps = {
  children: React.ReactNode;
};

export default function LegalAcceptanceGate({ children }: LegalAcceptanceGateProps) {
  const location = useLocation();
  const { token, getToken, error: tokenError } = useAuthToken();
  const { user, activeTenantId } = useUser();
  const [statusState, setStatusState] = useState<{
    tenantId: string;
    status: LegalStatusResponse;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");

  const status = statusState?.tenantId === activeTenantId ? statusState.status : null;

  const api = useMemo(() => {
    if (!token || !activeTenantId || !user?.userId) return null;
    return createApiClient({
      baseUrl: protectedResources.baseURL.endpoint,
      token,
      getToken,
      tenantId: activeTenantId,
      userId: user.userId,
    });
  }, [token, getToken, activeTenantId, user?.userId]);

  const loadStatus = useCallback(async () => {
    if (!api || !activeTenantId) return;
    setLoading(true);
    setError("");
    try {
      const next = await api.get<LegalStatusResponse>("legal/status");
      setStatusState({ tenantId: activeTenantId, status: next });
      setAccepted(false);
    } catch (err: any) {
      setError(err?.message || "Unable to verify legal acceptance status.");
    } finally {
      setLoading(false);
    }
  }, [api, activeTenantId]);

  useEffect(() => {
    if (location.pathname === "/selecttenant") return;
    void loadStatus();
  }, [loadStatus, location.pathname]);

  const handleAccept = async () => {
    if (!api || !status || !activeTenantId) return;
    setAccepting(true);
    setError("");
    try {
      const payload: LegalAcceptRequest = {
        termsVersion: status.termsVersion ?? null,
        privacyVersion: status.privacyVersion ?? null,
        source: ACCEPTANCE_SOURCE,
      };
      const next = await api.post<LegalStatusResponse>("legal/accept", payload);
      setStatusState({ tenantId: activeTenantId, status: next });
      setAccepted(false);
    } catch (err: any) {
      setError(err?.message || "Unable to record legal acceptance.");
    } finally {
      setAccepting(false);
    }
  };

  if (location.pathname === "/selecttenant") return <>{children}</>;

  if (!status) {
    if (error || tokenError) {
      const message = error || tokenError || "Unable to verify legal acceptance status.";
      return (
        <LegalGateShell title="Legal acceptance unavailable">
          <div className={styles["legal-gate-error"]} role="alert">{message}</div>
          <p>
            We could not verify whether this workspace has accepted the current
            Terms of Service and Privacy Notice. Please retry before continuing.
          </p>
          <div className={styles["legal-gate-actions"]}>
            <button
              type="button"
              className={styles["legal-gate-primary"]}
              onClick={() => void loadStatus()}
              disabled={loading || !api}
            >
              Retry
            </button>
          </div>
        </LegalGateShell>
      );
    }
    return (
      <WorkspaceLoading message="We're checking your profile and tenant access." />
    );
  }

  if (!status?.requiresAcceptance) return <>{children}</>;

  if (!status.canAccept) {
    return (
      <LegalGateShell title="Terms acceptance required">
        <p>
          This workspace needs an Owner or Admin to accept the current Land
          Agility Terms of Service and Privacy Notice before users can continue.
        </p>
        <p className={styles["legal-gate-muted"]}>
          Please contact a workspace Owner or Admin and ask them to sign in and
          accept the updated documents.
        </p>
        <div className={styles["legal-gate-actions"]}>
          <Link className={styles["legal-gate-secondary"]} to="/terms" target="_blank" rel="noopener noreferrer">
            View Terms
          </Link>
          <Link className={styles["legal-gate-secondary"]} to="/privacy" target="_blank" rel="noopener noreferrer">
            View Privacy Notice
          </Link>
        </div>
      </LegalGateShell>
    );
  }

  return (
    <LegalGateShell title="Accept updated legal terms">
      {error && <div className={styles["legal-gate-error"]} role="alert">{error}</div>}
      <p>
        Before this workspace can continue, an Owner or Admin must accept the
        current Land Agility Terms of Service and Privacy Notice for the tenant.
      </p>
      <div className={styles["legal-gate-acceptance"]}>
        <input
          id="legalGateAcceptance"
          type="checkbox"
          checked={accepted}
          onChange={(event) => setAccepted(event.target.checked)}
          disabled={accepting}
        />
        <label
          className={styles["legal-gate-acceptance-text"]}
          htmlFor="legalGateAcceptance"
        >
          I am authorized to accept the{" "}
          <Link className={styles["legal-gate-link"]} to="/terms" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </Link>
          {" "}and{" "}
          <Link className={styles["legal-gate-link"]} to="/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Notice
          </Link>
          {" "}for this Land Agility workspace.
        </label>
      </div>
      <div className={styles["legal-gate-actions"]}>
        <button
          type="button"
          className={styles["legal-gate-primary"]}
          onClick={() => void handleAccept()}
          disabled={!accepted || accepting}
        >
          {accepting ? "Recording acceptance..." : "Accept and continue"}
        </button>
        <button
          type="button"
          className={styles["legal-gate-secondary"]}
          onClick={() => void loadStatus()}
          disabled={accepting || loading}
        >
          Refresh status
        </button>
      </div>
    </LegalGateShell>
  );
}

function LegalGateShell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className={styles["legal-gate-shell"]}>
      <section className={styles["legal-gate-card"]} aria-labelledby="legalGateTitle">
        <div className={styles["legal-gate-card-header"]}>
          <h1 id="legalGateTitle" className={styles["legal-gate-title"]}>{title}</h1>
        </div>
        <div className={styles["legal-gate-body"]}>{children}</div>
      </section>
    </div>
  );
}
